import React, { useRef, useState } from "react";

import { Container } from "@components/Container/Container";

import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Navigation } from "swiper";

import styles from "./styles.module.scss";
import { ImageField } from "@helpers/interfaces/image";
import { Box } from "@components/Box/Box";
import { LinkField } from "@helpers/interfaces/link";
import { UmbracoImage } from "@components/Image/UmbracoImage/UmbracoImage";
import { Link } from "@components/Link/Link";
import { Text } from "@components/Text/Text";
import { Icon } from "@components/Icon/Icon";
import arrowLeft from "@svg-icons/arrow-left.component.svg";
import arrowRight from "@svg-icons/arrow-right.component.svg";
import classNames from "classnames";
import { AspectRatio } from "@components/AspectRatio/AspectRatio";
import { Module } from "@components/Module/Module";
import { VideoField } from "@helpers/interfaces/media";
import { UmbracoVideo } from "@components/UmbracoVideo/UmbracoVideo";
import stylesHeading from "@components/Heading/styles.module.scss";
import { Heading } from "@components/Heading/Heading";

SwiperCore.use([Navigation]);

interface slideProps {
  Key: string;
  Media: {
    Video: VideoField;
    Image: ImageField;
  }[];
  Link: LinkField;
  ImageText?: string;
  index: number;
  currentSlide?: number;
  callback?: () => void;
}

export interface SliderProps {
  Key: string;
  headline: string;
  slides: slideProps[];
}

const InnerContent: React.FC<slideProps> = ({ ...slide }) => {
  if (slide.Media[0].Image) {
    return (
      <UmbracoImage
        image={slide.Media[0].Image}
        width={[350, 850, 850, 850]}
        onLoad={slide.callback}
        isCover
      />
    );
  } else if (slide.Media[0].Video) {
    return (
      <AspectRatio ratio={["16:9"]}>
        {slide.index === slide.currentSlide ? (
          <UmbracoVideo video={slide.Media[0].Video} />
        ) : (
          <></>
        )}
      </AspectRatio>
    );
  }

  return <></>;
};

const Content: React.FC<slideProps> = ({ ...slide }) => {
  if (!slide.Media.length) return <></>;
  return (
    <>
      {slide.Link ? (
        <Link
          href={slide.Link.Url}
          {...(slide.Link.Target && {
            target: slide.Link.Target
          })}
        >
          <InnerContent {...slide} />
        </Link>
      ) : (
        <InnerContent {...slide} />
      )}
      {slide.ImageText && (
        <Text className={styles.imageText}>{slide.ImageText}</Text>
      )}
    </>
  );
};

const Slider: React.FC<SliderProps> = ({ headline, slides, ...props }) => {
  const [currentSlide, setCurrentSlide] = useState<number>(0);

  const swiperChange = (swiper: SwiperCore) => {
    setCurrentSlide(swiper.activeIndex);
  };

  const swiperRef = useRef<SwiperCore>();
  const initSwiper = (swiper: SwiperCore) => {
    swiperRef.current = swiper;
  };

  const updateSwiperHeight = () => {
    if (swiperRef.current) swiperRef.current.updateAutoHeight();
  };

  const firstSlide = slides[0];
  return (
    <Module animate>
      <Container variant="medium" smallpad>
        {headline && (
          <Heading as="h2" className={stylesHeading.headingRibbon}>
            {headline}
          </Heading>
        )}
        {slides.length === 1 ? (
          <Content {...firstSlide} />
        ) : (
          <Swiper
            className={styles.swiperContainer}
            navigation={{
              nextEl: `.swiper-button-next-${props.Key}`,
              prevEl: `.swiper-button-prev-${props.Key}`
            }}
            autoHeight
            onSlideChange={swiper => swiperChange(swiper)}
            onSwiper={swiper => initSwiper(swiper)}
          >
            {slides &&
              slides.map((slide: slideProps, index: number) => {
                return (
                  <SwiperSlide key={slide.Key} className={styles.swiperSlide}>
                    <Content
                      {...{
                        ...slide,
                        index: index,
                        currentSlide: currentSlide,
                        callback: updateSwiperHeight
                      }}
                    />
                  </SwiperSlide>
                );
              })}
            <Box className={styles.swiperNavigation}>
              <button
                className={classNames(
                  styles.swiperButton,
                  `swiper-button-prev-${props.Key}`
                )}
              >
                <Icon svg={arrowLeft} />
              </button>
              <button
                className={classNames(
                  styles.swiperButton,
                  `swiper-button-next-${props.Key}`
                )}
              >
                <Icon svg={arrowRight} />
              </button>
            </Box>
          </Swiper>
        )}
      </Container>
    </Module>
  );
};
export { Slider };
